const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

const midiFrequency = (pitch) => 440 * (2 ** ((Number(pitch) - 69) / 12));
const setParam = (param, value, when) => param?.setValueAtTime?.(value, when);
const linearParam = (param, value, when) => param?.linearRampToValueAtTime?.(value, when);
const exponentialParam = (param, value, when) => param?.exponentialRampToValueAtTime?.(Math.max(0.0001, value), when);

export function samplePlaybackRate({ pitch = 60, rootNote = 60, tune = 0, fine = 0 } = {}) {
  return 2 ** ((Number(pitch) - Number(rootNote) + Number(tune) + Number(fine) / 100) / 12);
}

export function playSample(ctx, destination, spec, { pitch = spec.rootNote ?? 60, when = 0, duration = Infinity, velocity = 1 } = {}) {
  if (!spec?.buffer) return null;
  const source = ctx.createBufferSource();
  const gain = ctx.createGain();
  const rate = samplePlaybackRate({ pitch, rootNote: spec.rootNote ?? 60, tune: spec.tune, fine: spec.fine });
  source.buffer = spec.buffer;
  setParam(source.playbackRate, rate, when);
  const attack = clamp(spec.attack, 0.001, 0.5, 0.005);
  const release = clamp(spec.release, 0.01, 2, 0.15);
  setParam(gain.gain, 0, when);
  linearParam(gain.gain, Math.max(0, Number(velocity)), when + attack);
  const naturalDuration = spec.buffer.duration / rate;
  const gated = spec.gate !== false && Number.isFinite(duration);
  const audibleDuration = Math.min(naturalDuration, gated ? Math.max(attack, Number(duration)) + release : naturalDuration);
  if (gated) {
    const releaseAt = when + Math.min(Math.max(attack, Number(duration)), audibleDuration);
    setParam(gain.gain, Math.max(0, Number(velocity)), releaseAt);
    linearParam(gain.gain, 0, when + audibleDuration);
  }
  source.connect(gain);
  gain.connect(destination);
  source.start(when);
  source.stop(when + audibleDuration + 0.001);
  return {
    source, sources: [source], gain, playbackRate: rate, audibleEnd: when + audibleDuration,
    setPitch(nextPitch, at, glideMs = 0) {
      const nextRate = samplePlaybackRate({ pitch: nextPitch, rootNote: spec.rootNote ?? 60, tune: spec.tune, fine: spec.fine });
      setParam(source.playbackRate, source.playbackRate.value || rate, at);
      linearParam(source.playbackRate, nextRate, at + Math.max(0, glideMs) / 1000);
    },
    stop(at = ctx.currentTime) {
      if (!gated) return;
      setParam(gain.gain, gain.gain.value || 0, at);
      linearParam(gain.gain, 0, at + release);
      try { source.stop(at + release + 0.001); } catch (error) {}
    },
  };
}

export function playDrumHit(ctx, destination, buffer, { when = 0, velocity = 1, volume = 1 } = {}) {
  return playSample(ctx, destination, { buffer, rootNote: 60, attack: 0.001, release: 0.01, gate: false }, {
    pitch: 60,
    when,
    velocity: Math.max(0, Number(velocity)) * Math.max(0, Number(volume)),
  });
}

function saturationCurve() {
  const curve = new Float32Array(256);
  for (let index = 0; index < curve.length; index += 1) {
    const x = index * 2 / (curve.length - 1) - 1;
    curve[index] = Math.tanh(2.2 * x);
  }
  return curve;
}

function synthVoice(ctx, destination, spec, { pitch = 60, when = 0, duration = 0.25, velocity = 0.8 } = {}) {
  const voice = spec.voice || 'keys';
  const attack = clamp(spec.attack, 0.001, 0.5, voice === 'strings' ? 0.18 : 0.005);
  const release = clamp(spec.release, 0.01, 2, voice === '808' ? 0.8 : 0.15);
  const end = when + Math.max(0.001, Number(duration));
  const stopAt = end + release;
  const envelope = ctx.createGain();
  const peak = Math.max(0, Number(velocity));
  setParam(envelope.gain, 0, when);
  linearParam(envelope.gain, peak, when + attack);
  setParam(envelope.gain, peak, end);
  linearParam(envelope.gain, 0, stopAt);
  envelope.connect(destination);
  const sources = [];
  const pitchParams = [];
  const connectOsc = (type, detune = 0, output = envelope, ratio = 1) => {
    const oscillator = ctx.createOscillator();
    oscillator.type = type;
    setParam(oscillator.frequency, midiFrequency(pitch) * ratio, when);
    setParam(oscillator.detune, detune, when);
    oscillator.connect(output); oscillator.start(when); oscillator.stop(stopAt + 0.01);
    sources.push(oscillator); pitchParams.push({ param: oscillator.frequency, ratio });
    return oscillator;
  };

  if (voice === 'keys') {
    const carrier = connectOsc('sine');
    const modGain = ctx.createGain();
    setParam(modGain.gain, midiFrequency(pitch) * 1.5, when);
    const modulator = connectOsc('sine', 0, modGain, 2);
    modGain.connect(carrier.frequency);
    pitchParams.push({ param: modGain.gain, ratio: 1.5, amplitude: true });
    // Keep a reference so test doubles do not treat it as unused.
    void modulator;
  } else if (voice === 'strings') {
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; setParam(filter.frequency, 3200, when); filter.connect(envelope);
    connectOsc('sawtooth', -9, filter); connectOsc('sawtooth', 0, filter); connectOsc('sawtooth', 9, filter);
  } else if (voice === '808') {
    const shaper = ctx.createWaveShaper(); shaper.curve = saturationCurve(); shaper.oversample = '2x'; shaper.connect(envelope);
    const oscillator = connectOsc('sine', 0, shaper);
    setParam(oscillator.frequency, midiFrequency(pitch) * 1.45, when);
    exponentialParam(oscillator.frequency, midiFrequency(pitch), when + 0.045);
  } else if (voice === 'fingerbass') {
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; setParam(filter.frequency, 1800, when); exponentialParam(filter.frequency, 280, Math.min(end, when + 0.22)); filter.connect(envelope);
    connectOsc('triangle', 0, filter);
    const sawGain = ctx.createGain(); sawGain.gain.value = 0.22; sawGain.connect(filter); connectOsc('sawtooth', -12, sawGain);
  } else {
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; setParam(filter.frequency, 5200, when); exponentialParam(filter.frequency, 420, Math.min(end, when + 0.16)); filter.connect(envelope);
    connectOsc('sawtooth', 0, filter);
  }

  const handle = {
    source: sources[0], sources, gain: envelope, audibleEnd: stopAt,
    setPitch(nextPitch, at, glideMs = 0) {
      const until = at + Math.max(0, Number(glideMs)) / 1000;
      for (const entry of pitchParams) {
        const value = entry.amplitude ? midiFrequency(nextPitch) * entry.ratio : midiFrequency(nextPitch) * entry.ratio;
        setParam(entry.param, entry.param.value || value, at);
        linearParam(entry.param, value, until);
      }
    },
    stop(at = ctx.currentTime) {
      setParam(envelope.gain, envelope.gain.value || 0, at);
      linearParam(envelope.gain, 0, at + release);
      for (const source of sources) { try { source.stop(at + release + 0.01); } catch (error) {} }
    },
  };
  return handle;
}

export function buildMonoPhrases(notes, glideMs = 0) {
  const sorted = (notes || []).map((note) => ({ ...note, start: Number(note.start), length: Number(note.length) }))
    .filter((note) => Number.isFinite(note.start) && Number.isFinite(note.length) && note.length > 0)
    .sort((a, b) => a.start - b.start || a.pitch - b.pitch);
  const phrases = [];
  for (const note of sorted) {
    const event = { ...note, end: note.start + note.length };
    const phrase = phrases[phrases.length - 1];
    const previous = phrase?.notes[phrase.notes.length - 1];
    if (!phrase || note.start >= previous.originalEnd) {
      phrases.push({ start: note.start, end: event.end, notes: [{ ...event, originalEnd: event.end }], glides: [] });
      continue;
    }
    previous.end = note.start;
    phrase.glides.push({ at: note.start, fromPitch: previous.pitch, toPitch: note.pitch, durationMs: Math.max(0, Number(glideMs) || 0) });
    phrase.notes.push({ ...event, originalEnd: event.end });
    phrase.end = Math.max(phrase.end, event.end);
  }
  return phrases;
}

export function playMonoPhrase(ctx, destination, voiceSpec, phrase, { beatSeconds = 1 } = {}) {
  if (!phrase?.notes?.length) return null;
  const first = phrase.notes[0];
  const when = phrase.start * beatSeconds;
  const duration = (phrase.end - phrase.start) * beatSeconds;
  const handle = playNote(ctx, destination, voiceSpec, { pitch: first.pitch, when, duration, velocity: first.velocity });
  if (!handle) return null;
  for (const glide of phrase.glides) handle.setPitch?.(glide.toPitch, glide.at * beatSeconds, glide.durationMs);
  return handle;
}

/** One stable contract for live audition and offline rendering. */
export function playNote(ctx, destination, voiceSpec, options) {
  if (voiceSpec?.buffer) return playSample(ctx, destination, voiceSpec, options);
  if (['keys', 'strings', '808', 'fingerbass', 'pluck'].includes(voiceSpec?.voice)) return synthVoice(ctx, destination, voiceSpec, options);
  return null;
}

/** Last-note-priority realtime mono audition. Releasing the top key returns to the held key below it. */
export class MonoVoice {
  constructor(ctx, destination, voiceSpec) {
    this.ctx = ctx; this.destination = destination; this.voiceSpec = voiceSpec; this.held = []; this.handle = null;
  }
  noteOn(pitch, velocity = 0.8, when = this.ctx.currentTime) {
    this.held = this.held.filter((entry) => entry.pitch !== pitch);
    this.held.push({ pitch, velocity });
    if (!this.handle) this.handle = playNote(this.ctx, this.destination, this.voiceSpec, { pitch, velocity, when, duration: 3600 });
    else this.handle.setPitch?.(pitch, when, this.voiceSpec.glide || 0);
    return this.handle;
  }
  noteOff(pitch, when = this.ctx.currentTime) {
    this.held = this.held.filter((entry) => entry.pitch !== pitch);
    const previous = this.held[this.held.length - 1];
    if (previous) this.handle?.setPitch?.(previous.pitch, when, this.voiceSpec.glide || 0);
    else { this.handle?.stop?.(when); this.handle = null; }
  }
  stop(when = this.ctx.currentTime) { this.held = []; this.handle?.stop?.(when); this.handle = null; }
}
